// Library imports
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";

// User Component imports
import { PageContent, PageHeader } from 'components/common/layout_element/LayoutElement';
import { Row, Stack, Text, TextInput, Button, Modal, Icon } from "_core/components/common/UI";
import * as Constants from "_core/configs/Constants";
import * as SceneConfig from "_core/configs/SceneConfig";
import { canCreateTenant } from "_core/utils/common";
import { generatePassword } from "_core/utils/password";
import { setLoading, setErrorModal } from "_core/slices/scene/SceneSlice";
import AdminOpsTenantCreateApi from "apis/AdminOpsTenantCreateApi";
import { useOperatorPage } from "./common/useOperatorPage";
import OpsDomainListField, { useDomainRows } from "./common/OpsDomainListField";
import { useCopyUrl } from "./demo/useCopyUrl";
import {
    OPERATOR_PASSWORD_LENGTH, PASSWORD_MIN_LENGTH, PASSWORD_MAX_LENGTH,
    operatorEmailOf, tenantFormErrors, hasFormErrors, tenantCreateParams, buildCustomerMemo, buildOperatorMemo, buildAllMemo,
    CUSTOMER_MEMO_TITLE, OPERATOR_MEMO_TITLE,
} from "./utils/opsTenant";
import { joinDomains } from "./utils/opsDomain";

import './OpsCommon.scss';
import './AdminOpsTenant.scss';

const COPY_KEY_CUSTOMER = "customer";
const COPY_KEY_OPERATOR = "operator";
const COPY_KEY_ALL = "all";

const emptyForm = () => ({
    companyName: "",
    adminEmail: "",
    adminPassword: generatePassword(),
    operatorName: "",
});

const AdminOpsTenant = () => {
    const dispatch = useDispatch();
    const ExecCreate = AdminOpsTenantCreateApi();

    const [form, setForm] = useState(emptyForm);
    const domainRows = useDomainRows();
    // 運営用パスワードは画面で作る（応答が届かず作成だけ成功した場合でも、画面に残った値で入れるように）
    const [operatorPassword, setOperatorPassword] = useState(() => generatePassword(OPERATOR_PASSWORD_LENGTH));
    // 入力欄を作り直して初期値を反映するための版（TextInput は default を初回しか読まない）
    const [formVersion, setFormVersion] = useState(0);
    const [touched, setTouched] = useState({});
    const [showAllErrors, setShowAllErrors] = useState(false);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [result, setResult] = useState(null);
    const [uncertain, setUncertain] = useState(null);

    const { canUse } = useOperatorPage(() => {}, canCreateTenant);

    const showError = (response) => {
        dispatch(setErrorModal({ show: true, text: response?.error_detail ?? { message: Constants.UNKNOWN_ERROR_DETAIL } }));
    };
    const { copiedKey, copyUrl } = useCopyUrl(() => {
        showError({ error_detail: { title: "エラー", message: "クリップボードへのコピーに失敗しました。" } });
    });

    // 作成結果（パスワード入り）を表示している間は、ページを離れる前に確認する
    useEffect(() => {
        if (!result) return undefined;
        const onBeforeUnload = (e) => {
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, [result]);

    const values = { ...form, domains: domainRows.values };
    const operatorEmail = operatorEmailOf(values.adminEmail, values.operatorName);
    const errors = tenantFormErrors(values);
    const errorOf = (key) => (showAllErrors || touched[key] ? errors[key] : "");
    const touch = (key) => setTouched((prev) => ({ ...prev, [key]: true }));

    const setValue = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
    const regenerateAdminPassword = () => {
        setValue("adminPassword", generatePassword());
        touch("adminPassword");
    };

    const openConfirm = () => {
        if (hasFormErrors(errors)) {
            setShowAllErrors(true);
            return;
        }
        setIsConfirmOpen(true);
    };

    const create = async () => {
        setIsConfirmOpen(false);
        if (hasFormErrors(errors)) return;
        dispatch(setLoading(true));
        const { response } = await ExecCreate(tenantCreateParams(values, operatorPassword));
        dispatch(setLoading(false));

        // 通信が途切れた場合はサーバーで作成済みの可能性がある
        if (!response || response.error_code === 404) {
            setUncertain({ operatorEmail, operatorPassword });
            return;
        }
        if (response.error_code !== 0) {
            showError(response);
            return;
        }
        setUncertain(null);
        setResult({ response, adminPassword: form.adminPassword, operatorPassword });
    };

    const restart = () => {
        setForm(emptyForm());
        domainRows.reset([]);
        setOperatorPassword(generatePassword(OPERATOR_PASSWORD_LENGTH));
        setTouched({});
        setShowAllErrors(false);
        setUncertain(null);
        setResult(null);
        setFormVersion((v) => v + 1);
    };

    const fieldError = (key) => errorOf(key) && <div className="ops-setting-error">{errorOf(key)}</div>;

    const renderInput = (key, { label, placeholder, type = "text", onChange, value }) => (
        <div className="ops-tenant-input">
            {label && <div className="ops-tenant-input-label">{label}</div>}
            <TextInput parentClassName={"ops-tenant-text" + (errorOf(key) ? " is-error" : "")} type={type}
                default={form[key]} value={value} placeholder={placeholder} autocomplete="off" autocorrect={false}
                onchange={(e) => (onChange ?? ((v) => setValue(key, v)))(e.target.value)} onblur={() => touch(key)} />
            {fieldError(key)}
        </div>
    );

    const renderRow = (label, hint, children) => (
        <div className="ops-setting-row">
            <div className="ops-setting-label">
                <Text className="ops-setting-label-text">{label}</Text>
                {hint && <div className="ops-setting-label-hint">{hint}</div>}
            </div>
            <div className="ops-setting-field">{children}</div>
        </div>
    );

    const renderForm = () => (
        <div className="ops-setting-card" key={formVersion}>
            {renderRow("会社名", "管理画面に表示される会社名です。\n同じ名前の会社が既にあれば、その会社に追加されます。",
                renderInput("companyName", { placeholder: "株式会社〇〇" }))}
            <hr />
            {renderRow("許可ドメイン", "ウィジェット・対話画面を設置できるドメインです。\n*.example.com でサブドメインをまとめて許可できます。",
                <OpsDomainListField domainRows={domainRows} showListError={showAllErrors} />)}
            <hr />
            {renderRow("管理者アカウント", "お客様に渡すアカウントです（権限: 管理者）。\nアカウント名は会社名になります。",
                <>
                    {renderInput("adminEmail", { label: "メールアドレス", placeholder: "name@example.com", type: "email" })}
                    <div className="ops-tenant-input">
                        <div className="ops-tenant-input-label">初期パスワード</div>
                        <div className="ops-tenant-password relative">
                            <TextInput parentClassName={"ops-tenant-text" + (errorOf("adminPassword") ? " is-error" : "")}
                                className="has-regenerate-button" default={form.adminPassword} value={form.adminPassword}
                                autocomplete="new-password" autocorrect={false}
                                onchange={(e) => setValue("adminPassword", e.target.value)} onblur={() => touch("adminPassword")} />
                            <Button className="ops-tenant-regenerate" onClick={regenerateAdminPassword} title="パスワードを再生成">
                                <Icon icon="arrow-clockwise" size={18} />
                            </Button>
                        </div>
                        <div className="ops-setting-sub">{PASSWORD_MIN_LENGTH}文字以上{PASSWORD_MAX_LENGTH}文字以下。作成後の控えに載ります。</div>
                        {fieldError("adminPassword")}
                    </div>
                </>)}
            <hr />
            {renderRow("運営用アカウント", "あなたがこのテナントに入るためのアカウントです（権限: 運営）。\n管理者のメールアドレスに「+名前」を付けて作ります。\nパスワードは作成後に表示します。",
                <>
                    {renderInput("operatorName", { label: "名前（名の頭文字＋姓をローマ字・小文字で）", placeholder: "例: msatou（佐藤 まなみ）" })}
                    <div className="ops-tenant-operator-email">
                        <span className="ops-tenant-input-label">メールアドレス</span>
                        {operatorEmail ? <code>{operatorEmail}</code> : <span className="ops-setting-sub">管理者のメールアドレスと名前を入れると表示されます</span>}
                    </div>
                </>)}
        </div>
    );

    const renderMemo = (key, title, text) => (
        <div className="ops-tenant-memo">
            <div className="ops-tenant-memo-head">
                <div className="ops-tenant-memo-title">{title}</div>
                <button type="button" className="ops-tenant-memo-copy" onClick={() => copyUrl(key, text)}>
                    <i className={"bi " + (copiedKey === key ? "bi-check-lg" : "bi-copy")} />{copiedKey === key ? "コピーしました" : "コピー"}
                </button>
            </div>
            <pre className="ops-tenant-memo-text">{text}</pre>
        </div>
    );

    const renderResult = () => {
        const { response, adminPassword } = result;
        const loginUrl = window.location.origin + SceneConfig.PAGES.ACCOUNT_TOP;
        const tenant = response.tenant;
        return (
            <div className="ops-tenant-result">
                <div className="ops-tenant-result-head">
                    <div className="ops-tenant-result-title"><i className="bi bi-check-circle-fill" />テナントを作成しました</div>
                    <span className="ops-tenant-result-badge">成功</span>
                </div>
                <div className="ops-tenant-result-summary">
                    <div><span>テナントID</span>{tenant.t_user_id}</div>
                    <div><span>会社</span>{tenant.company_name}（{tenant.is_company_created ? "新規" : "既存の会社に追加"}・会社ID {tenant.m_company_id}）</div>
                    <div><span>許可ドメイン</span>{tenant.domains.join(", ")}</div>
                </div>
                <div className="ops-tenant-result-warning">
                    <i className="bi bi-exclamation-triangle-fill" />パスワードはこの画面でしか確認できません。ページを離れる前に控えをコピーしてください。
                </div>
                {renderMemo(COPY_KEY_CUSTOMER, CUSTOMER_MEMO_TITLE, buildCustomerMemo(response, adminPassword, loginUrl))}
                {renderMemo(COPY_KEY_OPERATOR, OPERATOR_MEMO_TITLE, buildOperatorMemo(response, result.operatorPassword, loginUrl))}
                <div className="ops-tenant-result-actions">
                    <button type="button" className="ops-tenant-result-btn"
                        onClick={() => copyUrl(COPY_KEY_ALL, buildAllMemo(response, adminPassword, result.operatorPassword, loginUrl))}>
                        <i className={"bi " + (copiedKey === COPY_KEY_ALL ? "bi-check-lg" : "bi-copy")} />{copiedKey === COPY_KEY_ALL ? "コピーしました" : "まとめてコピー"}
                    </button>
                    <button type="button" className="ops-tenant-result-btn is-primary" onClick={restart}>
                        <i className="bi bi-plus-lg" />続けて作成
                    </button>
                </div>
            </div>
        );
    };

    const confirmBody = (
        <Stack className="ops-tenant-confirm spacing-12px padding-16px">
            <Text>次の内容でテナントを作成します。</Text>
            <div className="ops-tenant-confirm-list">
                <div><span>会社名</span>{form.companyName}</div>
                <div><span>許可ドメイン</span>{joinDomains(domainRows.values).split(",").join(", ")}</div>
                <div><span>管理者</span>{form.adminEmail}</div>
                <div><span>運営用</span>{operatorEmail}</div>
            </div>
            <Text className="ops-tenant-confirm-note">※運営用アカウントのパスワードは、作成後に一度だけ表示します。</Text>
        </Stack>
    );

    return (
        <PageContent pageId="AdminOpsTenant">
            <div className="ops-scope width-full">
                <PageHeader>
                    <Row className="flex-both align-center width-full">
                        <Stack className="spacing-4px">
                            <div className="page-header-title">テナント作成</div>
                            <div className="page-header-detail">新しい会社のテナントを、お客様の管理者アカウントとあなたの運営用アカウント付きで作成します。社内ネットワークからのみ作成できます。</div>
                        </Stack>
                        {!result &&
                            <Button className="ops-tenant-create-button width-240px primary" disabled={!canUse} onClick={openConfirm}>
                                作成
                            </Button>
                        }
                    </Row>
                </PageHeader>

                <div className="ops-tenant-body">
                    {uncertain &&
                        <div className="ops-tenant-uncertain">
                            <div className="ops-tenant-uncertain-title"><i className="bi bi-exclamation-triangle-fill" />通信に失敗しました</div>
                            <div>テナントは作成済みの可能性があります。管理者のメールアドレスでログインできるか確認してから、再度作成してください（作成済みなら「既に使用されています」と表示されます）。</div>
                            <div>作成済みだった場合の運営用アカウント: <code>{uncertain.operatorEmail}</code> / パスワード: <code>{uncertain.operatorPassword}</code></div>
                        </div>
                    }
                    {result ? renderResult() : renderForm()}
                </div>

                <Modal
                    className={isConfirmOpen ? "show" : ""}
                    dialogclass="modal-dialog-centered"
                    name="ops-tenant-confirm-modal"
                    title="テナントを作成"

                    showClose={false}

                    showCancel={true}
                    cancel={() => setIsConfirmOpen(false)}
                    cancelbtnclass="secondary-border"
                    cancelvalue="キャンセル"

                    showConfirm={true}
                    confirm={create}
                    confirmbtnclass="primary"
                    confirmvalue="作成"

                    body={confirmBody}
                />
            </div>
        </PageContent>
    );
};

export default AdminOpsTenant;
